import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Checkbox } from "../components/ui/checkbox";
import { Star, Clock, ArrowRight, Sparkles } from "lucide-react";
import { toast } from "sonner";
import * as Icons from "lucide-react";

export default function CategoryDetail() {
  const { id } = useParams();
  const { user, refresh } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/categories/${id}`).then(({ data }) => setData(data));
  }, [id]);

  const completed = new Set(user?.completed_tasks || []);

  const toggle = async (task) => {
    const isDone = completed.has(task.task_id);
    try {
      if (isDone) {
        await api.post("/tasks/uncomplete", { task_id: task.task_id });
        toast("Unchecked");
      } else {
        const { data: r } = await api.post("/tasks/complete", { task_id: task.task_id });
        toast.success(`+${r.points_earned} XP earned!`);
        if (r.leveled_up) setTimeout(() => toast.success(`🎉 Level up! You're now level ${r.user.level}`), 800);
        if (r.new_badges?.length) setTimeout(() => toast.success(`✨ New badge: ${r.new_badges[0]}`), 1400);
      }
      await refresh();
    } catch {
      toast.error("Couldn't update");
    }
  };

  if (!data) return <div className="text-white/60">Loading...</div>;
  const { category, tasks } = data;
  const Icon = Icons[category.icon] || Icons.Sparkles;
  const done = tasks.filter(t => completed.has(t.task_id)).length;
  const pct = tasks.length ? Math.round((done / tasks.length) * 100) : 0;

  return (
    <div data-testid="category-detail-page">
      <Link to="/categories" className="text-xs text-white/50 hover:text-white">← All categories</Link>
      <div className="glass-card p-6 md:p-8 mt-3">
        <div className="flex flex-col md:flex-row md:items-center gap-5">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center"
               style={{ background: `${category.color}22`, color: category.color, boxShadow: `0 0 26px ${category.color}40` }}>
            <Icon size={28}/>
          </div>
          <div className="flex-1">
            <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight">{category.name}</h1>
            <p className="text-white/65 mt-1">{category.description}</p>
          </div>
        </div>
        <div className="mt-6">
          <div className="flex justify-between text-xs text-white/60 mb-1.5">
            <span>{done} of {tasks.length} complete</span><span>{pct}%</span>
          </div>
          <div className="h-2 rounded-full bg-white/10 overflow-hidden">
            <div className="h-full transition-all" style={{ width: `${pct}%`, background: category.color }}/>
          </div>
        </div>
      </div>

      <div className="space-y-3 mt-6">
        {tasks.map(t => {
          const isDone = completed.has(t.task_id);
          return (
            <div key={t.task_id} className={`glass-card p-5 flex items-start gap-4 transition ${isDone ? "opacity-70" : ""}`} data-testid={`task-row-${t.task_id}`}>
              <Checkbox
                checked={isDone}
                onCheckedChange={() => toggle(t)}
                data-testid={`task-check-${t.task_id}`}
                className="mt-1.5 size-6 rounded-full data-[state=checked]:bg-neongreen data-[state=checked]:border-neongreen border-white/20"
              />
              <Link to={`/tasks/${t.task_id}`} className="flex-1 min-w-0">
                <div className={`font-display font-bold text-base ${isDone ? "line-through text-white/50" : ""}`}>{t.title}</div>
                <div className="text-sm text-white/60 mt-1 line-clamp-2">{t.why}</div>
                <div className="flex flex-wrap gap-3 mt-3 text-xs text-white/55">
                  <span className="flex items-center gap-1"><Clock size={12}/> {t.estimated_time}</span>
                  <span className="capitalize">{t.difficulty}</span>
                  <span className="flex items-center gap-1 text-neonyellow font-bold"><Star size={12}/> +{t.points} XP</span>
                </div>
              </Link>
              <Link to={`/tasks/${t.task_id}`} className="self-center w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center" data-testid={`task-open-${t.task_id}`}>
                <ArrowRight size={16}/>
              </Link>
            </div>
          );
        })}
      </div>
    </div>
  );
}
